/**
 * Reporte de cumplimiento, exportable a PDF -- a pedido del DPD: "poder sacar
 * reportes... de las unidades totales o algún reporte de cumplimiento de la
 * normativa". No hay backend nuevo: reusa fetchInstitutionalDashboard/
 * fetchUnitDashboard/fetchActivities (ya construidos para los tableros) y
 * los presenta en una vista pensada para imprimirse -- `window.print()` +
 * el CSS `@media print` que ya traía el proyecto (styles.css, nunca antes
 * usado por ninguna pantalla) oculta automáticamente el sidebar/topbar/
 * botones (`.no-print`) sin tocar AppShell.
 *
 * Dos alcances, misma plantilla:
 *  - /reportes            -- institucional: agregado por unidad (para no
 *    imprimir cientos de filas de actividad en un solo documento).
 *  - /reportes/unidad/:id -- una unidad: detalle actividad por actividad,
 *    con las mismas banderas de riesgo que ya expone /activities.
 */
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Bar, BarChart, CartesianGrid, Cell, LabelList, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { AXIS_TICK, clip, TIP, TONE_COLOR } from '../components/chartTheme';
import { stageCounts } from '../lib/tableros';
import { can, isMemberOf, ACTIVITY_STATUS_LABEL, type ActivityStatus, type UnitRatStatus } from '@rat/shared';
import {
  fetchActivities,
  fetchInstitutionalDashboard,
  fetchUnitDashboard,
  type ActivityRow,
} from '../lib/queries';
import { useAuth } from '../auth/AuthProvider';
import { QueryState, UnitStatusPill } from '../components/ui';
import { Wordmark } from '../components/Logo';
import { AttentionTable, RiskKpis } from '../components/DashboardWidgets';
import { Unauthorized } from './Misc';

function generatedFooter(email?: string) {
  const now = new Date();
  return `Generado automáticamente por el sistema RAT el ${now.toLocaleDateString('es-CL')} a las ${now.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })}${email ? ` por ${email}` : ''}.`;
}

export function Report() {
  const { unitId } = useParams();
  const { authz, session } = useAuth();

  const canSeeInstitutional = can(authz, 'institutional.view') || can(authz, 'unit.read.all');
  const canSeeThisUnit = unitId ? isMemberOf(authz, unitId) || canSeeInstitutional : false;
  if (unitId ? !canSeeThisUnit : !canSeeInstitutional) return <Unauthorized />;

  return unitId ? <UnitReport unitId={unitId} email={session?.user.email} /> : <InstitutionalReport email={session?.user.email} />;
}

function ReportHeader({ scope }: { scope: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 4 }}>
      <Wordmark size={34} />
      <div>
        <div style={{ fontFamily: 'var(--f-display)', fontWeight: 700, fontSize: 17 }}>
          Reporte de cumplimiento — Registro de Actividades de Tratamiento
        </div>
        <div className="muted" style={{ fontSize: 12 }}>
          Ley 21.719 sobre Protección de Datos Personales · Alcance: {scope}
        </div>
      </div>
    </div>
  );
}

function ReportFooter({ email }: { email?: string }) {
  return (
    <p className="muted" style={{ fontSize: 10.5, marginTop: 24, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
      {generatedFooter(email)}
    </p>
  );
}

function PrintBar() {
  return (
    <div className="no-print" style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
      <button className="btn btn--primary" onClick={() => window.print()}>
        Imprimir / exportar PDF
      </button>
    </div>
  );
}

/**
 * Completitud promedio por unidad, ordenada de mayor a menor. Una sola serie (magnitud) = un solo tono y sin leyenda;
 * el valor va rotulado al final de cada barra porque este reporte se imprime y en papel no hay tooltip.
 */
function UnitProgressChart({ rows }: { rows: Array<{ unit_id: string; name: string; completeness_avg: number }> }) {
  const data = [...rows].sort((a, b) => b.completeness_avg - a.completeness_avg);
  const height = Math.max(160, data.length * 28 + 40);
  return (
    <div style={{ height }} role="img" aria-label="Completitud promedio de las actividades de cada unidad, en porcentaje">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ top: 4, right: 44, bottom: 0, left: 0 }} barCategoryGap="30%">
          <CartesianGrid horizontal={false} stroke="var(--border)" />
          <XAxis type="number" domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} tickFormatter={(v: number) => `${v}%`} tick={AXIS_TICK} axisLine={false} tickLine={false} />
          <YAxis type="category" dataKey="name" width={150} interval={0} tick={AXIS_TICK} tickFormatter={(v: string) => clip(v, 22)} axisLine={false} tickLine={false} />
          <Tooltip {...TIP} formatter={(v) => [`${v}%`, 'Completitud promedio']} />
          {/* minPointSize: una unidad en 0% igual muestra su rotulo "0%" (sin esto parecia un dato faltante) */}
          <Bar dataKey="completeness_avg" fill="var(--brand)" radius={[0, 4, 4, 0]} minPointSize={2} isAnimationActive={false}>
            <LabelList dataKey="completeness_avg" position="right" formatter={(v) => `${v}%`} style={{ fill: 'var(--ink)', fontSize: 11.5 }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * Actividades por etapa del flujo (las mismas 5 etapas y colores del tablero del administrador). Leyenda con cantidad
 * y % al lado: en papel no hay tooltip y el color solo nunca identifica una etapa.
 */
const DONUT_PX = 200;

function StageDonut({ byStatus }: { byStatus: Record<string, number> }) {
  const data = stageCounts(byStatus);
  const total = data.reduce((sum, d) => sum + d.n, 0);
  const pct = (n: number) => (total === 0 ? 0 : Math.round((n / total) * 100));
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {/* Tamano FIJO a proposito: ResponsiveContainer mide el ancho en pantalla y no vuelve a medir al imprimir, asi que en
          el PDF la torta quedaba descentrada respecto del total superpuesto. Una torta no necesita estirarse. */}
      <div style={{ position: 'relative', width: DONUT_PX, height: DONUT_PX, margin: '0 auto' }} role="img" aria-label={`Actividades por etapa: ${data.map((d) => `${d.etapa} ${d.n}`).join(', ')}`}>
        <PieChart width={DONUT_PX} height={DONUT_PX}>
          <Pie data={data.filter((d) => d.n > 0)} dataKey="n" nameKey="etapa" cx="50%" cy="50%" innerRadius={62} outerRadius={92} startAngle={90} endAngle={-270} stroke="var(--surface)" strokeWidth={2} isAnimationActive={false}>
            {data.filter((d) => d.n > 0).map((d) => (
              <Cell key={d.etapa} fill={TONE_COLOR[d.tone]} />
            ))}
          </Pie>
          <Tooltip {...TIP} formatter={(v, name) => [`${v} (${pct(Number(v))}%)`, name]} />
        </PieChart>
        <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', pointerEvents: 'none' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontFamily: 'var(--f-display)', fontSize: 32, fontWeight: 700, lineHeight: 1 }}>{total}</div>
            <div className="muted" style={{ fontSize: 12 }}>actividades</div>
          </div>
        </div>
      </div>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 5, fontSize: 12.5 }}>
        {data.map((d) => (
          <li key={d.etapa} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span aria-hidden style={{ width: 10, height: 10, borderRadius: 2, background: TONE_COLOR[d.tone], flex: 'none' }} />
            <span style={{ flex: 1 }}>{d.etapa}</span>
            <span className="mono">{d.n}</span>
            <span className="mono muted" style={{ width: 38, textAlign: 'right' }}>{pct(d.n)}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function InstitutionalReport({ email }: { email?: string }) {
  const q = useQuery({ queryKey: ['dash', 'inst'], queryFn: fetchInstitutionalDashboard });

  return (
    <div className="printable">
      <PrintBar />
      <QueryState isLoading={q.isLoading} error={q.error}>
        {q.data && (
          <>
            <ReportHeader scope="institucional (todas las unidades)" />

            <section style={{ marginTop: 18 }}>
              <h3 style={{ fontSize: 13.5 }}>Resumen</h3>
              <div className="kpis" style={{ marginTop: 8 }}>
                <div className="kpi">
                  <div className="n">{q.data.unidades_total}</div>
                  <div className="l">Unidades</div>
                </div>
                <div className="kpi">
                  <div className="n">{q.data.actividades_total}</div>
                  <div className="l">Actividades</div>
                </div>
                {(Object.keys(q.data.por_estado) as ActivityStatus[]).map((s) => (
                  <div className="kpi" key={s}>
                    <div className="n">{q.data!.por_estado[s]}</div>
                    <div className="l">{ACTIVITY_STATUS_LABEL[s]}</div>
                  </div>
                ))}
              </div>
            </section>

            <div className="report-charts" style={{ marginTop: 18 }}>
              <section className="card report-chart">
                <div className="card__b">
                  <h3 style={{ fontSize: 13.5 }}>Actividades por etapa</h3>
                  <p className="muted" style={{ fontSize: 12, margin: '2px 0 10px' }}>En qué punto del flujo está cada actividad.</p>
                  <StageDonut byStatus={q.data.por_estado} />
                </div>
              </section>
              <section className="card report-chart">
                <div className="card__b">
                  <h3 style={{ fontSize: 13.5 }}>Avance por unidad</h3>
                  <p className="muted" style={{ fontSize: 12, margin: '2px 0 10px' }}>Completitud promedio de las actividades de cada unidad.</p>
                  <UnitProgressChart rows={q.data.por_unidad} />
                </div>
              </section>
            </div>

            <section style={{ marginTop: 18 }}>
              <h3 style={{ fontSize: 13.5 }}>Riesgo y cumplimiento</h3>
              <RiskKpis riesgo={q.data.riesgo} />
            </section>

            <section style={{ marginTop: 18 }}>
              <h3 style={{ fontSize: 13.5 }}>Necesitan atención (Observado hace +14 días)</h3>
              <AttentionTable rows={q.data.atencion} showUnit />
            </section>

            <section style={{ marginTop: 18 }}>
              <h3 style={{ fontSize: 13.5 }}>Avance por unidad</h3>
              <div className="tablewrap" style={{ border: 0, marginTop: 8 }}>
                <table className="data">
                  <thead>
                    <tr>
                      <th>Unidad</th>
                      <th>Estado</th>
                      <th>Actividades</th>
                      <th>Completitud</th>
                    </tr>
                  </thead>
                  <tbody>
                    {q.data.por_unidad.map((u) => (
                      <tr key={u.unit_id}>
                        <td>{u.name}</td>
                        <td>
                          <UnitStatusPill status={u.rat_status as UnitRatStatus} />
                        </td>
                        <td className="mono">{u.activities_total}</td>
                        <td className="mono">{u.completeness_avg}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <ReportFooter email={email} />
          </>
        )}
      </QueryState>
    </div>
  );
}

function UnitReport({ unitId, email }: { unitId: string; email?: string }) {
  const dash = useQuery({ queryKey: ['dash', 'unit', unitId], queryFn: () => fetchUnitDashboard(unitId) });
  const acts = useQuery({ queryKey: ['activities', 'unit', unitId], queryFn: () => fetchActivities({ unitId, limit: 200 }) });

  return (
    <div className="printable">
      <PrintBar />
      <QueryState isLoading={dash.isLoading} error={dash.error}>
        {dash.data && (
          <>
            <ReportHeader scope={dash.data.unit_name} />

            <section style={{ marginTop: 18 }}>
              <h3 style={{ fontSize: 13.5 }}>Resumen</h3>
              <div className="kpis" style={{ marginTop: 8 }}>
                <div className="kpi">
                  <div className="n">{dash.data.activities_total}</div>
                  <div className="l">Actividades</div>
                </div>
                <div className="kpi">
                  <div className="n">{dash.data.completeness_avg}%</div>
                  <div className="l">Completitud promedio</div>
                </div>
                <div className="kpi">
                  <div className="n">{dash.data.observaciones_abiertas}</div>
                  <div className="l">Observaciones abiertas</div>
                </div>
                {(Object.keys(dash.data.by_status) as ActivityStatus[]).map((s) => (
                  <div className="kpi" key={s}>
                    <div className="n">{dash.data!.by_status[s]}</div>
                    <div className="l">{ACTIVITY_STATUS_LABEL[s]}</div>
                  </div>
                ))}
              </div>
            </section>

            <section style={{ marginTop: 18 }}>
              <h3 style={{ fontSize: 13.5 }}>Riesgo y cumplimiento</h3>
              <RiskKpis riesgo={dash.data.riesgo} />
            </section>

            <section style={{ marginTop: 18 }}>
              <h3 style={{ fontSize: 13.5 }}>Necesitan atención (Observado hace +14 días)</h3>
              <AttentionTable rows={dash.data.atencion} />
            </section>

            <section style={{ marginTop: 18 }}>
              <h3 style={{ fontSize: 13.5 }}>Detalle de actividades</h3>
              <QueryState isLoading={acts.isLoading} error={acts.error}>
                <div className="tablewrap" style={{ border: 0, marginTop: 8 }}>
                  <table className="data">
                    <thead>
                      <tr>
                        <th>Código</th>
                        <th>Actividad</th>
                        <th>Estado</th>
                        <th>Completitud</th>
                        <th>Banderas</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(acts.data ?? []).map((a: ActivityRow) => (
                        <tr key={a.id}>
                          <td className="mono">{a.ref_code}</td>
                          <td>{a.title || '(sin nombre)'}</td>
                          <td>{ACTIVITY_STATUS_LABEL[a.status]}</td>
                          <td className="mono">{a.completeness_pct}%</td>
                          <td style={{ fontSize: 11 }}>
                            {[
                              a.has_sensitive_data && 'Datos sensibles',
                              a.has_international_transfer && 'Transf. internacional',
                              a.has_automated_decision && 'Decisión automatizada',
                            ]
                              .filter(Boolean)
                              .join(' · ') || '—'}
                          </td>
                        </tr>
                      ))}
                      {acts.data?.length === 0 && (
                        <tr>
                          <td colSpan={5} className="muted" style={{ padding: 16 }}>
                            Esta unidad aún no registra actividades de tratamiento.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </QueryState>
            </section>

            <ReportFooter email={email} />
          </>
        )}
      </QueryState>
    </div>
  );
}
